import React, { useEffect, useState, useRef } from 'react';
import { LoggedInNav } from '../components/LoggedInNav';
import { CalendarIcon, UploadIcon, RefreshCwIcon, UsersIcon, ClipboardIcon, ClockIcon, CheckCircleIcon, ExternalLinkIcon, ChevronRightIcon, MailIcon, AlertTriangleIcon, XIcon, FileTextIcon, BuildingIcon, UserIcon, PlusIcon, CheckIcon, BrainIcon, LockIcon, UnlockIcon, SearchIcon, BarChartIcon, ArrowRightIcon, LaptopIcon, ShieldCheckIcon, Loader2Icon, LinkIcon, ZapIcon } from 'lucide-react';
type ExtractedData = {
  subject: string;
  dateTime: string;
  attendees: {
    name: string;
    email: string;
    role?: string;
    company?: string;
  }[];
  description: string;
};
type GeneratedBrief = {
  companyInsights: {
    name: string;
    description: string;
    recentNews: string[];
    linkedInUrl?: string;
    isMock: boolean;
    confidenceLevel: 'High' | 'Medium' | 'Low';
  }[];
  attendeeBreakdown: {
    name: string;
    role: string;
    company: string;
    linkedInUrl?: string;
    background?: string;
    isRoleConfirmed: boolean;
  }[];
  personaSection: {
    type: 'sales' | 'customerService' | 'consultant';
    content: any;
  };
  previousEngagement: {
    interactions: {
      date: string;
      summary: string;
    }[];
    isLimited: boolean;
  };
  transcriptInsights: {
    hasPreviousTranscripts: boolean;
    outstandingCommitments: string[];
    pendingItems: string[];
    resolvedItems: string[];
    sentimentHighlights: string[];
  };
};
export function Simulation() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [generatedBrief, setGeneratedBrief] = useState<GeneratedBrief | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<'sales' | 'customerService' | 'consultant'>('sales');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Animation states for the process flow
  const [animationStep, setAnimationStep] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showLightning, setShowLightning] = useState(false);
  const [briefReveal, setBriefReveal] = useState(false);
  // Advance the animation sequence
  useEffect(() => {
    if (animationStep < 4) {
      const timer = setTimeout(() => {
        setAnimationStep(animationStep + 1);
      }, animationStep === 0 ? 3000 : 4500);
      return () => clearTimeout(timer);
    } else {
      // When animation reaches step 4, trigger lightning effect
      setTimeout(() => {
        setShowLightning(true);
        // After lightning effect, show the brief
        setTimeout(() => {
          setShowLightning(false);
          setBriefReveal(true);
          // After brief is revealed, complete the animation
          setTimeout(() => {
            setAnimationComplete(true);
            // Mock extracted data (in a real app, this would come from OCR/AI processing)
            const mockExtractedData: ExtractedData = {
              subject: 'Quarterly Business Review - Acme Corp',
              dateTime: 'Thursday, August 12, 2023 • 10:00 AM - 11:30 AM',
              attendees: [{
                name: 'John Smith',
                email: 'john.smith@acmecorp.com',
                role: 'CEO',
                company: 'Acme Corp'
              }, {
                name: 'Sarah Johnson',
                email: 'sarah.j@acmecorp.com',
                role: 'VP of Operations',
                company: 'Acme Corp'
              }, {
                name: 'Michael Wong',
                email: 'm.wong@acmecorp.com',
                role: 'Product Manager',
                company: 'Acme Corp'
              }, {
                name: 'Lisa Chen',
                email: 'l.chen@yourcompany.com',
                role: 'Account Executive',
                company: 'Your Company'
              }, {
                name: 'David Miller',
                email: 'd.miller@yourcompany.com',
                role: 'Customer Success',
                company: 'Your Company'
              }],
              description: 'Quarterly business review to discuss Q2 results, implementation progress, and roadmap for next quarter. Please bring the latest metrics and be prepared to discuss the API integration timeline.'
            };
            setExtractedData(mockExtractedData);
          }, 1000);
        }, 1500);
      }, 1000);
    }
  }, [animationStep]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setExtractedData(null);
      setGeneratedBrief(null);
      processImage(e.target.files[0]);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setExtractedData(null);
      setGeneratedBrief(null);
      processImage(e.dataTransfer.files[0]);
    }
  };
  const processImage = (file: File) => {
    setIsProcessing(true);
    // Simulate processing time
    setTimeout(() => {
      // Mock extracted data (in a real app, this would come from OCR/AI processing)
      const mockExtractedData: ExtractedData = {
        subject: 'Quarterly Business Review - Acme Corp',
        dateTime: 'Thursday, August 12, 2023 • 10:00 AM - 11:30 AM',
        attendees: [{
          name: 'John Smith',
          email: 'john.smith@acmecorp.com',
          role: 'CEO',
          company: 'Acme Corp'
        }, {
          name: 'Sarah Johnson',
          email: 'sarah.j@acmecorp.com',
          role: 'VP of Operations',
          company: 'Acme Corp'
        }, {
          name: 'Michael Wong',
          email: 'm.wong@acmecorp.com',
          role: 'Product Manager',
          company: 'Acme Corp'
        }, {
          name: 'Lisa Chen',
          email: 'l.chen@yourcompany.com',
          role: 'Account Executive',
          company: 'Your Company'
        }, {
          name: 'David Miller',
          email: 'd.miller@yourcompany.com',
          role: 'Customer Success',
          company: 'Your Company'
        }],
        description: 'Quarterly business review to discuss Q2 results, implementation progress, and roadmap for next quarter. Please bring the latest metrics and be prepared to discuss the API integration timeline.'
      };
      setExtractedData(mockExtractedData);
      setIsProcessing(false);
    }, 2000);
  };
  const handleGenerateBrief = () => {
    if (!extractedData) return;
    setIsGeneratingBrief(true);
    // Simulate API call to generate brief based on the prompt
    setTimeout(() => {
      // Mock generated brief (in a real app, this would come from an AI model)
      const mockGeneratedBrief: GeneratedBrief = {
        companyInsights: [{
          name: 'Acme Corporation',
          description: 'Leading provider of business software solutions with a focus on enterprise resource planning and supply chain management.',
          recentNews: ['Announced partnership with Microsoft for cloud integration (June 2023)', 'Released new version of flagship product AcmeERP 4.0 with enhanced AI capabilities (May 2023)', 'Expanded operations to European market with new office in Berlin (April 2023)'],
          linkedInUrl: 'https://linkedin.com/company/acmecorp',
          isMock: false,
          confidenceLevel: 'High'
        }],
        attendeeBreakdown: [{
          name: 'John Smith',
          role: 'Chief Executive Officer',
          company: 'Acme Corp',
          linkedInUrl: 'https://linkedin.com/in/johnsmith',
          background: '15+ years in enterprise software. Previously CTO at TechGiant Inc. MBA from Stanford.',
          isRoleConfirmed: true
        }, {
          name: 'Sarah Johnson',
          role: 'VP of Operations',
          company: 'Acme Corp',
          linkedInUrl: 'https://linkedin.com/in/sarahjohnson',
          background: 'Oversees implementation teams and customer success. Joined Acme 3 years ago from Oracle.',
          isRoleConfirmed: true
        }, {
          name: 'Michael Wong',
          role: 'Product Manager',
          company: 'Acme Corp',
          linkedInUrl: 'https://linkedin.com/in/michaelwong',
          background: 'Responsible for the AcmeERP platform. Technical background in software development.',
          isRoleConfirmed: true
        }, {
          name: 'Lisa Chen',
          role: 'Account Executive',
          company: 'Your Company',
          background: 'Primary point of contact for Acme Corp for the past 2 years.',
          isRoleConfirmed: true
        }, {
          name: 'David Miller',
          role: 'Customer Success Manager',
          company: 'Your Company',
          background: "Supporting Acme Corp's implementation since January 2023.",
          isRoleConfirmed: true
        }],
        personaSection: {
          type: selectedPersona,
          content: selectedPersona === 'sales' ? {
            potentialObjections: ['Cost of upgrading to AcmeERP 4.0 may be perceived as too high', 'Timeline for API integration may not meet their Q3 goals', 'Concerns about training requirements for new features'],
            discoveryQuestions: ['What are your key priorities for Q3 and Q4?', "How has the implementation been progressing from your team's perspective?", 'What challenges are you facing with the current integration process?', 'What would success look like for this partnership by end of year?']
          } : selectedPersona === 'customerService' ? {
            openCases: [{
              id: 'CS-2023-1245',
              title: 'API Authentication Issues',
              priority: 'High',
              openedDate: 'July 28, 2023'
            }, {
              id: 'CS-2023-1102',
              title: 'Custom Report Generation',
              priority: 'Medium',
              openedDate: 'July 15, 2023'
            }],
            customerHealth: {
              score: 'Yellow (72/100)',
              notes: 'Implementation delayed by 2 weeks due to resource constraints on client side. User adoption at 65% vs 80% target.'
            },
            supportRisks: ['Integration with legacy systems taking longer than expected', 'Training completion rate below target (65% vs 90%)']
          } : {
            discoveryFraming: 'Focus on understanding operational bottlenecks and KPI measurement challenges',
            risks: ['Resource allocation for API integration may be insufficient', 'Data migration complexity underestimated', 'Executive alignment on success metrics unclear'],
            deliverables: ['Implementation roadmap with clear milestones', 'Training program for 200+ users', 'Custom reporting dashboard for executive visibility'],
            kpis: ['Reduce order processing time by 40%', 'Improve inventory accuracy to 98%', 'Decrease manual data entry by 75%']
          }
        },
        previousEngagement: {
          interactions: [{
            date: 'June 15, 2023',
            summary: 'Initial implementation planning meeting. Acme team expressed concerns about timeline.'
          }, {
            date: 'May 28, 2023',
            summary: 'Contract signing. John Smith emphasized importance of API integration for their Q3 goals.'
          }, {
            date: 'May 10, 2023',
            summary: 'Product demo with technical team. Positive feedback on new features.'
          }, {
            date: 'April 22, 2023',
            summary: 'Needs assessment call. Identified pain points in current workflow.'
          }],
          isLimited: false
        },
        transcriptInsights: {
          hasPreviousTranscripts: true,
          outstandingCommitments: ["Provide custom API documentation for Acme's development team (due Aug 15)", 'Schedule training session for operations team (pending date confirmation)'],
          pendingItems: ['Acme to provide access to test environment', 'Review of custom report templates'],
          resolvedItems: ['Pricing structure finalized and approved', 'Initial user accounts created and configured'],
          sentimentHighlights: ['John Smith (CEO) focused on ROI and implementation timeline', 'Sarah Johnson concerned about user adoption and training', 'Michael Wong enthusiastic about technical capabilities but worried about integration complexity']
        }
      };
      setGeneratedBrief(mockGeneratedBrief);
      setIsGeneratingBrief(false);
    }, 3000);
  };
  const addAttendee = () => {
    if (!extractedData) return;
    setExtractedData({
      ...extractedData,
      attendees: [...extractedData.attendees, {
        name: '',
        email: ''
      }]
    });
  };
  const updateAttendee = (index: number, field: keyof ExtractedData['attendees'][0], value: string) => {
    if (!extractedData) return;
    const updatedAttendees = [...extractedData.attendees];
    updatedAttendees[index] = {
      ...updatedAttendees[index],
      [field]: value
    };
    setExtractedData({
      ...extractedData,
      attendees: updatedAttendees
    });
  };
  const removeAttendee = (index: number) => {
    if (!extractedData) return;
    const updatedAttendees = [...extractedData.attendees];
    updatedAttendees.splice(index, 1);
    setExtractedData({
      ...extractedData,
      attendees: updatedAttendees
    });
  };
  const resetForm = () => {
    setFile(null);
    setExtractedData(null);
    setGeneratedBrief(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  return <div className="w-full min-h-screen bg-[#1E2A38] text-[#F5F7FA] font-sans" data-id="element-3585">
      <LoggedInNav data-id="element-3586" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3587">
        <div className="flex justify-between items-center mb-8" data-id="element-3588">
          <div data-id="element-3589">
            <h1 className="text-2xl font-bold mb-2" data-id="element-3590">Calendar Simulation</h1>
            <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3591">
              See how Prepz.io automatically prepares your meeting briefs from
              calendar events
            </p>
          </div>
        </div>
        <div className="space-y-8" data-id="element-3592">
          {/* Step 1: Animated Process Flow */}
          <div className="bg-[#1A2331] rounded-xl p-6" data-id="element-3593">
            <h2 className="text-xl font-semibold mb-6 flex items-center" data-id="element-3594">
              <CalendarIcon className="mr-2 h-5 w-5 text-[#C8F169]" data-id="element-3595" />
              How Prepz Works
            </h2>
            {/* Animated process flow container - Even taller height */}
            <div className="relative h-[900px] overflow-hidden rounded-lg border border-[#2A3544] shadow-lg bg-[#141E2A]" data-id="element-3596">
              {/* Step indicator */}
              <div className="absolute top-6 left-6 z-20 bg-[#1A2331] rounded-full px-4 py-1.5 text-xs font-medium text-[#F5F7FA] border border-[#2A3544] flex items-center" data-id="element-3597">
                <span className="text-[#C8F169] mr-2" data-id="element-3598">
                  {animationStep === 0 && 'Step 1'}
                  {animationStep === 1 && 'Step 2'}
                  {animationStep === 2 && 'Step 3'}
                  {animationStep === 3 && 'Step 4'}
                  {animationStep === 4 && 'Complete'}
                </span>
                <span className="text-[#F5F7FA] text-opacity-70" data-id="element-3599">
                  {animationStep === 0 && 'Authentication'}
                  {animationStep === 1 && 'Calendar Sync'}
                  {animationStep === 2 && 'AI Processing'}
                  {animationStep === 3 && 'Brief Generation'}
                  {animationStep === 4 && 'Meeting Ready'}
                </span>
              </div>
              {/* Step 1: Authentication */}
              <div className={`absolute inset-0 transition-all duration-1500 ease-in-out ${animationStep === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}`} data-id="element-3600">
                <div className="h-full flex flex-col items-center justify-center bg-[#141E2A] p-10" data-id="element-3601">
                  <div className="w-full max-w-xl mx-auto" data-id="element-3602">
                    <div className="text-center mb-10" data-id="element-3603">
                      <div className="w-20 h-20 bg-[#1A2331] rounded-full mx-auto mb-6 flex items-center justify-center" data-id="element-3604">
                        <LockIcon className="h-8 w-8 text-[#C8F169]" data-id="element-3605" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2" data-id="element-3606">
                        Connect Your Accounts
                      </h3>
                      <p className="text-[#F5F7FA] text-opacity-70 max-w-md mx-auto" data-id="element-3607">
                        Securely connect your accounts to get started with
                        Prepz. We use OAuth for secure authentication.
                      </p>
                    </div>
                    <div className="space-y-5 mb-8" data-id="element-3608">
                      <div className="bg-[#1A2331] p-6 rounded-lg shadow-sm border border-[#2A3544] hover:border-[#C8F169] transition-colors" data-id="element-3609">
                        <div className="flex items-center" data-id="element-3610">
                          <div className="w-12 h-12 rounded-full bg-[#1E2A38] flex items-center justify-center mr-5" data-id="element-3611">
                            <CalendarIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3612" />
                          </div>
                          <div className="flex-1" data-id="element-3613">
                            <h5 className="font-medium text-lg" data-id="element-3614">
                              Google Calendar
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3615">
                              Connect to automatically prepare for meetings
                            </p>
                          </div>
                          <button className="bg-[#C8F169] hover:bg-opacity-90 transition-colors text-[#1E2A38] px-4 py-2 rounded-lg font-medium flex items-center" data-id="element-3616">
                            Connect
                          </button>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-6 rounded-lg shadow-sm border border-[#2A3544] hover:border-[#C8F169] transition-colors" data-id="element-3617">
                        <div className="flex items-center" data-id="element-3618">
                          <div className="w-12 h-12 rounded-full bg-[#1E2A38] flex items-center justify-center mr-5" data-id="element-3619">
                            <MailIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3620" />
                          </div>
                          <div className="flex-1" data-id="element-3621">
                            <h5 className="font-medium text-lg" data-id="element-3622">Gmail</h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3623">
                              Connect to analyze previous communications
                            </p>
                          </div>
                          <button className="bg-[#C8F169] hover:bg-opacity-90 transition-colors text-[#1E2A38] px-4 py-2 rounded-lg font-medium flex items-center" data-id="element-3624">
                            Connect
                          </button>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-6 rounded-lg shadow-sm border border-[#2A3544]" data-id="element-3625">
                        <div className="flex items-center opacity-50" data-id="element-3626">
                          <div className="w-12 h-12 rounded-full bg-[#1E2A38] flex items-center justify-center mr-5" data-id="element-3627">
                            <LaptopIcon className="h-6 w-6 text-[#F5F7FA]" data-id="element-3628" />
                          </div>
                          <div className="flex-1" data-id="element-3629">
                            <h5 className="font-medium text-lg" data-id="element-3630">
                              Microsoft Outlook
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3631">
                              Connect your Outlook calendar and emails
                            </p>
                          </div>
                          <button className="bg-[#1E2A38] text-[#F5F7FA] text-opacity-70 px-4 py-2 rounded-lg font-medium flex items-center" data-id="element-3632">
                            Coming Soon
                          </button>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-6 rounded-lg shadow-sm border border-[#2A3544]" data-id="element-3633">
                        <div className="flex items-center opacity-50" data-id="element-3634">
                          <div className="w-12 h-12 rounded-full bg-[#1E2A38] flex items-center justify-center mr-5" data-id="element-3635">
                            <BuildingIcon className="h-6 w-6 text-[#F5F7FA]" data-id="element-3636" />
                          </div>
                          <div className="flex-1" data-id="element-3637">
                            <h5 className="font-medium text-lg" data-id="element-3638">Salesforce</h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3639">
                              Connect to pull customer and account data
                            </p>
                          </div>
                          <button className="bg-[#1E2A38] text-[#F5F7FA] text-opacity-70 px-4 py-2 rounded-lg font-medium flex items-center" data-id="element-3640">
                            Coming Soon
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-5 bg-[#1A2331] rounded-lg border border-[#2A3544]" data-id="element-3641">
                      <div className="flex items-center" data-id="element-3642">
                        <ShieldCheckIcon className="h-5 w-5 text-[#C8F169] mr-3" data-id="element-3643" />
                        <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3644">
                          Your data is encrypted and secure. We only access
                          what's needed to prepare your meetings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Step 2: Calendar Sync */}
              <div className={`absolute inset-0 transition-all duration-1500 ease-in-out ${animationStep === 1 ? 'opacity-100 translate-y-0' : animationStep < 1 ? 'opacity-0 translate-y-full' : 'opacity-0 -translate-y-full'}`} data-id="element-3645">
                <div className="h-full flex flex-col items-center justify-center bg-[#141E2A] p-10" data-id="element-3646">
                  <div className="w-full max-w-3xl mx-auto" data-id="element-3647">
                    <div className="text-center mb-8" data-id="element-3648">
                      <div className="w-16 h-16 bg-[#1A2331] rounded-full mx-auto mb-6 flex items-center justify-center" data-id="element-3649">
                        <UnlockIcon className="h-7 w-7 text-[#C8F169]" data-id="element-3650" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2" data-id="element-3651">
                        Connected Successfully
                      </h3>
                      <p className="text-[#F5F7FA] text-opacity-70 max-w-md mx-auto" data-id="element-3652">
                        We're syncing your upcoming meetings. This will only
                        take a moment.
                      </p>
                    </div>
                    {/* Calendar container */}
                    <div className="bg-[#1A2331] rounded-xl border border-[#2A3544] shadow-lg overflow-hidden" data-id="element-3653">
                      {/* Calendar header */}
                      <div className="bg-[#1E2A38] p-5 border-b border-[#2A3544] flex items-center justify-between" data-id="element-3654">
                        <div className="flex items-center" data-id="element-3655">
                          <CalendarIcon className="h-5 w-5 text-[#C8F169] mr-2" data-id="element-3656" />
                          <h4 className="font-medium" data-id="element-3657">August 2023</h4>
                        </div>
                        <div className="flex space-x-2" data-id="element-3658">
                          <button className="w-8 h-8 rounded-lg bg-[#141E2A] flex items-center justify-center" data-id="element-3659">
                            <ChevronRightIcon className="h-4 w-4 rotate-180" data-id="element-3660" />
                          </button>
                          <button className="w-8 h-8 rounded-lg bg-[#141E2A] flex items-center justify-center" data-id="element-3661">
                            <ChevronRightIcon className="h-4 w-4" data-id="element-3662" />
                          </button>
                        </div>
                      </div>
                      {/* Calendar body */}
                      <div className="p-6" data-id="element-3663">
                        <div className="grid grid-cols-7 gap-2 mb-4" data-id="element-3664">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => <div key={i} className="text-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3665">
                              {day}
                            </div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-2" data-id="element-3666">
                          {Array.from({
                          length: 31
                        }).map((_, i) => <div key={i} className={`h-14 flex items-center justify-center rounded-lg text-sm
                                ${i === 11 ? 'bg-[#C8F169] text-[#1A2331] font-medium' : i > 0 && i < 12 ? 'bg-[#1E2A38]' : 'text-[#F5F7FA] text-opacity-30'}`} data-id="element-3667">
                              {i + 1}
                              {i === 11 && <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full bg-[#1A2331]" data-id="element-3668"></div>}
                            </div>)}
                        </div>
                      </div>
                      {/* Upcoming meetings section */}
                      <div className="p-6 border-t border-[#2A3544]" data-id="element-3669">
                        <h4 className="font-medium mb-4 flex items-center" data-id="element-3670">
                          <ClockIcon className="h-4 w-4 mr-2 text-[#C8F169]" data-id="element-3671" />
                          Upcoming Meetings
                        </h4>
                        <div className="space-y-3" data-id="element-3672">
                          {/* Highlighted meeting being extracted */}
                          <div className="bg-[#1E2A38] rounded-lg p-4 border-l-4 border-[#C8F169] shadow-md" data-id="element-3673">
                            <div className="flex items-center" data-id="element-3674">
                              <div className="w-12 h-12 rounded-lg bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-lg font-bold mr-4" data-id="element-3675">
                                AC
                              </div>
                              <div data-id="element-3676">
                                <h5 className="font-medium text-lg" data-id="element-3677">
                                  Quarterly Business Review - Acme Corp
                                </h5>
                                <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3678">
                                  <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-3679" />
                                  Thursday, Aug 12 • 10:00 AM - 11:30 AM
                                </div>
                              </div>
                              <div className="ml-auto" data-id="element-3680">
                                <div className="px-3 py-1 bg-[#C8F169] bg-opacity-10 text-[#C8F169] text-sm rounded-full" data-id="element-3681">
                                  Syncing...
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Other meetings */}
                          <div className="bg-[#1E2A38] rounded-lg p-4 opacity-50" data-id="element-3682">
                            <div className="flex items-center" data-id="element-3683">
                              <div className="w-12 h-12 rounded-lg bg-[#1E2A38] flex items-center justify-center text-[#F5F7FA] text-lg font-bold mr-4" data-id="element-3684">
                                TS
                              </div>
                              <div data-id="element-3685">
                                <h5 className="font-medium" data-id="element-3686">Team Standup</h5>
                                <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3687">
                                  <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-3688" />
                                  Thursday, Aug 12 • 9:00 AM - 9:30 AM
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#1E2A38] rounded-lg p-4 opacity-50" data-id="element-3689">
                            <div className="flex items-center" data-id="element-3690">
                              <div className="w-12 h-12 rounded-lg bg-[#1E2A38] flex items-center justify-center text-[#F5F7FA] text-lg font-bold mr-4" data-id="element-3691">
                                PM
                              </div>
                              <div data-id="element-3692">
                                <h5 className="font-medium" data-id="element-3693">Product Meeting</h5>
                                <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3694">
                                  <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-3695" />
                                  Thursday, Aug 12 • 2:00 PM - 3:00 PM
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#1E2A38] rounded-lg p-4 opacity-50" data-id="element-3696">
                            <div className="flex items-center" data-id="element-3697">
                              <div className="w-12 h-12 rounded-lg bg-[#1E2A38] flex items-center justify-center text-[#F5F7FA] text-lg font-bold mr-4" data-id="element-3698">
                                SC
                              </div>
                              <div data-id="element-3699">
                                <h5 className="font-medium" data-id="element-3700">
                                  Strategy Committee
                                </h5>
                                <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3701">
                                  <ClockIcon className="h-3.5 w-3.5 mr-1" data-id="element-3702" />
                                  Friday, Aug 13 • 11:00 AM - 12:00 PM
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Step 3: AI Processing */}
              <div className={`absolute inset-0 transition-all duration-1500 ease-in-out ${animationStep === 2 ? 'opacity-100 translate-y-0' : animationStep < 2 ? 'opacity-0 translate-y-full' : 'opacity-0 -translate-y-full'}`} data-id="element-3703">
                <div className="h-full flex flex-col items-center justify-center bg-[#141E2A] p-10" data-id="element-3704">
                  <div className="w-full max-w-2xl mx-auto" data-id="element-3705">
                    <div className="text-center mb-10" data-id="element-3706">
                      <div className="relative w-20 h-20 mx-auto mb-6" data-id="element-3707">
                        <div className="w-20 h-20 rounded-full bg-[#1A2331] flex items-center justify-center" data-id="element-3708">
                          <BrainIcon className="h-10 w-10 text-[#C8F169]" data-id="element-3709" />
                        </div>
                        <div className="absolute inset-0 rounded-full border-4 border-[#C8F169] border-opacity-20 border-t-[#C8F169] animate-spin" data-id="element-3710"></div>
                      </div>
                      <h3 className="text-2xl font-bold mb-2" data-id="element-3711">
                        Processing Meeting Brief
                      </h3>
                      <p className="text-[#F5F7FA] text-opacity-70 max-w-md mx-auto" data-id="element-3712">
                        Our AI is analyzing all relevant information to prepare
                        your meeting brief
                      </p>
                    </div>
                    {/* Processing steps */}
                    <div className="space-y-5" data-id="element-3713">
                      <div className="bg-[#1A2331] p-5 rounded-lg border border-[#2A3544]" data-id="element-3714">
                        <div className="flex items-start" data-id="element-3715">
                          <div className="w-10 h-10 rounded-lg bg-[#1E2A38] flex items-center justify-center mr-4 mt-1" data-id="element-3716">
                            <CheckCircleIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3717" />
                          </div>
                          <div data-id="element-3718">
                            <h5 className="font-medium text-lg mb-1" data-id="element-3719">
                              Gathering Attendee Information
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3720">
                              Collecting profiles, background information, and
                              recent activities for all meeting participants.
                            </p>
                            <div className="mt-3 w-full bg-[#1E2A38] h-1.5 rounded-full overflow-hidden" data-id="element-3721">
                              <div className="h-full bg-[#C8F169] w-full" data-id="element-3722"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-5 rounded-lg border border-[#2A3544]" data-id="element-3723">
                        <div className="flex items-start" data-id="element-3724">
                          <div className="w-10 h-10 rounded-lg bg-[#1E2A38] flex items-center justify-center mr-4 mt-1" data-id="element-3725">
                            <CheckCircleIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3726" />
                          </div>
                          <div data-id="element-3727">
                            <h5 className="font-medium text-lg mb-1" data-id="element-3728">
                              Researching Company Data
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3729">
                              Analyzing company information, recent news, and
                              market position.
                            </p>
                            <div className="mt-3 w-full bg-[#1E2A38] h-1.5 rounded-full overflow-hidden" data-id="element-3730">
                              <div className="h-full bg-[#C8F169] w-full" data-id="element-3731"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-5 rounded-lg border border-[#2A3544]" data-id="element-3732">
                        <div className="flex items-start" data-id="element-3733">
                          <div className="w-10 h-10 rounded-lg bg-[#1E2A38] flex items-center justify-center mr-4 mt-1" data-id="element-3734">
                            <CheckCircleIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3735" />
                          </div>
                          <div data-id="element-3736">
                            <h5 className="font-medium text-lg mb-1" data-id="element-3737">
                              Processing Previous Interactions
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3738">
                              Reviewing email history, past meetings, and
                              communications with attendees.
                            </p>
                            <div className="mt-3 w-full bg-[#1E2A38] h-1.5 rounded-full overflow-hidden" data-id="element-3739">
                              <div className="h-full bg-[#C8F169] w-full" data-id="element-3740"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-5 rounded-lg border border-[#2A3544]" data-id="element-3741">
                        <div className="flex items-start" data-id="element-3742">
                          <div className="w-10 h-10 rounded-lg bg-[#1E2A38] flex items-center justify-center mr-4 mt-1" data-id="element-3743">
                            <CheckCircleIcon className="h-6 w-6 text-[#C8F169]" data-id="element-3744" />
                          </div>
                          <div data-id="element-3745">
                            <h5 className="font-medium text-lg mb-1" data-id="element-3746">
                              Analyzing Meeting Context
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3747">
                              Determining meeting purpose, goals, and
                              relationship to previous interactions.
                            </p>
                            <div className="mt-3 w-full bg-[#1E2A38] h-1.5 rounded-full overflow-hidden" data-id="element-3748">
                              <div className="h-full bg-[#C8F169] w-4/5" data-id="element-3749"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1A2331] p-5 rounded-lg border border-[#2A3544] opacity-50" data-id="element-3750">
                        <div className="flex items-start" data-id="element-3751">
                          <div className="w-10 h-10 rounded-lg bg-[#1E2A38] flex items-center justify-center mr-4 mt-1" data-id="element-3752">
                            <FileTextIcon className="h-6 w-6 text-[#F5F7FA]" data-id="element-3753" />
                          </div>
                          <div data-id="element-3754">
                            <h5 className="font-medium text-lg mb-1" data-id="element-3755">
                              Generating Brief
                            </h5>
                            <p className="text-[#F5F7FA] text-opacity-70" data-id="element-3756">
                              Creating personalized meeting preparation with key
                              insights and talking points.
                            </p>
                            <div className="mt-3 w-full bg-[#1E2A38] h-1.5 rounded-full overflow-hidden" data-id="element-3757">
                              <div className="h-full bg-[#C8F169] w-0" data-id="element-3758"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-8 text-center" data-id="element-3759">
                      <p className="text-[#F5F7FA] text-opacity-50 text-sm" data-id="element-3760">
                        This typically takes less than a minute
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Step 4: Brief Generation */}
              <div className={`absolute inset-0 transition-all duration-1500 ease-in-out ${animationStep === 3 ? 'opacity-100 translate-y-0' : animationStep < 3 ? 'opacity-0 translate-y-full' : 'opacity-0 -translate-y-full'}`} data-id="element-3761">
                <div className="h-full bg-[#141E2A] overflow-y-auto" data-id="element-3762">
                  <div className="flex justify-center items-center h-full p-10" data-id="element-3763">
                    <div className="w-full max-w-3xl mx-auto" data-id="element-3764">
                      <div className="text-center mb-8" data-id="element-3765">
                        <div className="w-16 h-16 bg-[#1A2331] rounded-full mx-auto mb-6 flex items-center justify-center" data-id="element-3766">
                          <FileTextIcon className="h-7 w-7 text-[#C8F169]" data-id="element-3767" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2" data-id="element-3768">
                          Generating Your Brief
                        </h3>
                        <p className="text-[#F5F7FA] text-opacity-70 max-w-md mx-auto" data-id="element-3769">
                          Creating a comprehensive meeting preparation with all
                          essential information
                        </p>
                      </div>
                      {/* Brief preview being built */}
                      <div className="bg-[#1A2331] rounded-xl border border-[#2A3544] shadow-lg" data-id="element-3770">
                        {/* Header */}
                        <div className="p-6 border-b border-[#2A3544]" data-id="element-3771">
                          <div className="flex items-center justify-between mb-5" data-id="element-3772">
                            <div className="flex items-center" data-id="element-3773">
                              <div className="w-12 h-12 rounded-lg bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-lg font-bold mr-4" data-id="element-3774">
                                AC
                              </div>
                              <div data-id="element-3775">
                                <div className="h-6 w-64 bg-[#1E2A38] rounded-md mb-2" data-id="element-3776"></div>
                                <div className="h-4 w-40 bg-[#1E2A38] bg-opacity-70 rounded-md" data-id="element-3777"></div>
                              </div>
                            </div>
                            <div className="h-8 w-24 bg-[#1E2A38] rounded-full" data-id="element-3778"></div>
                          </div>
                          <div className="space-y-2" data-id="element-3779">
                            <div className="h-4 w-32 bg-[#1E2A38] rounded-md mb-2" data-id="element-3780"></div>
                            <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3781"></div>
                            <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3782"></div>
                            <div className="h-4 w-4/5 bg-[#1E2A38] rounded-md" data-id="element-3783"></div>
                          </div>
                        </div>
                        {/* Attendees section */}
                        <div className="p-6 border-b border-[#2A3544]" data-id="element-3784">
                          <div className="h-5 w-40 bg-[#1E2A38] rounded-md mb-5" data-id="element-3785"></div>
                          <div className="space-y-4" data-id="element-3786">
                            <div className="h-20 bg-[#1E2A38] rounded-lg" data-id="element-3787"></div>
                            <div className="h-20 bg-[#1E2A38] rounded-lg" data-id="element-3788"></div>
                            <div className="h-20 bg-[#1E2A38] rounded-lg" data-id="element-3789"></div>
                          </div>
                        </div>
                        {/* Business Insights section */}
                        <div className="p-6 border-b border-[#2A3544]" data-id="element-3790">
                          <div className="h-5 w-48 bg-[#1E2A38] rounded-md mb-5" data-id="element-3791"></div>
                          <div className="space-y-4" data-id="element-3792">
                            <div className="flex items-start" data-id="element-3793">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3794"></div>
                              <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3795"></div>
                            </div>
                            <div className="flex items-start" data-id="element-3796">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3797"></div>
                              <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3798"></div>
                            </div>
                            <div className="flex items-start" data-id="element-3799">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3800"></div>
                              <div className="h-4 w-4/5 bg-[#1E2A38] rounded-md" data-id="element-3801"></div>
                            </div>
                          </div>
                        </div>
                        {/* Talking Points section */}
                        <div className="p-6" data-id="element-3802">
                          <div className="h-5 w-36 bg-[#1E2A38] rounded-md mb-5" data-id="element-3803"></div>
                          <div className="space-y-4" data-id="element-3804">
                            <div className="flex items-start" data-id="element-3805">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3806"></div>
                              <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3807"></div>
                            </div>
                            <div className="flex items-start" data-id="element-3808">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3809"></div>
                              <div className="h-4 w-full bg-[#1E2A38] rounded-md" data-id="element-3810"></div>
                            </div>
                            <div className="flex items-start" data-id="element-3811">
                              <div className="h-6 w-6 rounded-full bg-[#1E2A38] mr-3 mt-1" data-id="element-3812"></div>
                              <div className="h-4 w-4/5 bg-[#1E2A38] rounded-md" data-id="element-3813"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-8 flex justify-center" data-id="element-3814">
                        <div className="flex items-center space-x-2" data-id="element-3815">
                          <Loader2Icon className="h-5 w-5 text-[#C8F169] animate-spin" data-id="element-3816" />
                          <span className="text-[#F5F7FA] text-opacity-70" data-id="element-3817">
                            Finalizing your brief...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Lightning bolt effect */}
              {showLightning && <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#141E2A] bg-opacity-60" data-id="element-3818">
                  <div className="relative" data-id="element-3819">
                    {/* Lightning bolt SVG */}
                    <svg width="200" height="200" viewBox="0 0 200 200" className="animate-pulse" data-id="element-3820">
                      <path d="M100 10 L120 80 L160 80 L100 190 L80 120 L40 120 Z" fill="#C8F169" className="animate-pulse" data-id="element-3821" />
                    </svg>
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-[#C8F169] filter blur-xl opacity-40 animate-pulse" data-id="element-3822"></div>
                    {/* Radial lines */}
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-3823">
                      {[...Array(8)].map((_, i) => <div key={i} className="absolute h-px w-full bg-[#C8F169] opacity-70 animate-pulse" style={{
                    transform: `rotate(${i * 45}deg)`,
                    transformOrigin: 'center',
                    animationDelay: `${i * 0.1}s`
                  }} data-id="element-3824"></div>)}
                    </div>
                  </div>
                </div>}
              {/* Step 5: Final Brief with reveal animation */}
              <div className={`absolute inset-0 transition-all duration-1500 ease-in-out ${animationStep === 4 && briefReveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`} data-id="element-3825">
                <div className="h-full bg-[#141E2A] overflow-y-auto p-8" data-id="element-3826">
                  <div className="max-w-4xl mx-auto" data-id="element-3827">
                    <div className="bg-[#1A2331] rounded-xl shadow-lg border border-[#2A3544] overflow-hidden animate-fadeIn" data-id="element-3828">
                      {/* Header */}
                      <div className="p-6 border-b border-[#2A3544]" data-id="element-3829">
                        <div className="flex items-center justify-between mb-6" data-id="element-3830">
                          <div className="flex items-center space-x-4" data-id="element-3831">
                            <div className="w-12 h-12 rounded-lg bg-[#C8F169] flex items-center justify-center text-[#1E2A38] font-bold text-lg" data-id="element-3832">
                              AC
                            </div>
                            <div data-id="element-3833">
                              <h3 className="font-medium text-xl" data-id="element-3834">
                                Quarterly Business Review - Acme Corp
                              </h3>
                              <div className="flex items-center text-[#F5F7FA] text-opacity-60 mt-1" data-id="element-3835">
                                <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-3836" />
                                <span data-id="element-3837">
                                  Thursday, August 12, 2023 • 10:00 AM - 11:30
                                  AM
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#C8F169] bg-opacity-10 px-3 py-1.5 rounded-lg" data-id="element-3838">
                            <span className="text-sm font-medium text-[#C8F169]" data-id="element-3839">
                              High Priority
                            </span>
                          </div>
                        </div>
                        <div data-id="element-3840">
                          <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-3" data-id="element-3841">
                            Meeting Overview
                          </h4>
                          <p className="text-[#F5F7FA] leading-relaxed" data-id="element-3842">
                            This quarterly business review will focus on
                            discussing Q2 results, implementation progress, and
                            roadmap for next quarter. The team will review API
                            integration timeline and address any outstanding
                            issues. Acme Corp is a key client who recently
                            expanded their contract by 25%, and they're
                            interested in additional service offerings.
                          </p>
                        </div>
                      </div>
                      {/* Attendees */}
                      <div className="p-6 border-b border-[#2A3544]" data-id="element-3843">
                        <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4 flex items-center" data-id="element-3844">
                          <UsersIcon className="h-4 w-4 mr-1.5 text-[#C8F169]" data-id="element-3845" />
                          Attendees (5)
                        </h4>
                        <div className="space-y-4" data-id="element-3846">
                          <div className="bg-[#1E2A38] p-4 rounded-lg" data-id="element-3847">
                            <div className="flex items-center justify-between" data-id="element-3848">
                              <div className="flex items-center space-x-4" data-id="element-3849">
                                <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-sm font-bold" data-id="element-3850">
                                  JS
                                </div>
                                <div data-id="element-3851">
                                  <div className="font-medium" data-id="element-3852">John Smith</div>
                                  <div className="text-sm text-[#F5F7FA] text-opacity-60 flex items-center" data-id="element-3853">
                                    <BuildingIcon className="h-3.5 w-3.5 mr-1" data-id="element-3854" />
                                    CEO, Acme Corp
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3" data-id="element-3855">
                                <a href="#" className="text-[#C8F169] flex items-center text-sm hover:underline" data-id="element-3856">
                                  <MailIcon className="h-4 w-4 mr-1" data-id="element-3857" />
                                  Email
                                </a>
                                <a href="#" className="text-[#C8F169] flex items-center text-sm hover:underline" data-id="element-3858">
                                  <LinkIcon className="h-4 w-4 mr-1" data-id="element-3859" />
                                  LinkedIn
                                </a>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#2A3544]" data-id="element-3860">
                              <div className="flex items-start" data-id="element-3861">
                                <ZapIcon className="h-4 w-4 text-[#C8F169] mt-0.5 mr-1.5 flex-shrink-0" data-id="element-3862" />
                                <p className="text-sm text-[#F5F7FA] text-opacity-80" data-id="element-3863">
                                  <span className="font-medium" data-id="element-3864">
                                    Key insight:
                                  </span>{' '}
                                  Recently spoke at SaaS Summit about API-first
                                  strategies. Previously worked at Microsoft for
                                  12 years.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#1E2A38] p-4 rounded-lg" data-id="element-3865">
                            <div className="flex items-center justify-between" data-id="element-3866">
                              <div className="flex items-center space-x-4" data-id="element-3867">
                                <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-sm font-bold" data-id="element-3868">
                                  SJ
                                </div>
                                <div data-id="element-3869">
                                  <div className="font-medium" data-id="element-3870">
                                    Sarah Johnson
                                  </div>
                                  <div className="text-sm text-[#F5F7FA] text-opacity-60 flex items-center" data-id="element-3871">
                                    <BuildingIcon className="h-3.5 w-3.5 mr-1" data-id="element-3872" />
                                    VP of Operations, Acme Corp
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3" data-id="element-3873">
                                <a href="#" className="text-[#C8F169] flex items-center text-sm hover:underline" data-id="element-3874">
                                  <MailIcon className="h-4 w-4 mr-1" data-id="element-3875" />
                                  Email
                                </a>
                                <a href="#" className="text-[#C8F169] flex items-center text-sm hover:underline" data-id="element-3876">
                                  <LinkIcon className="h-4 w-4 mr-1" data-id="element-3877" />
                                  LinkedIn
                                </a>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#2A3544]" data-id="element-3878">
                              <div className="flex items-start" data-id="element-3879">
                                <ZapIcon className="h-4 w-4 text-[#C8F169] mt-0.5 mr-1.5 flex-shrink-0" data-id="element-3880" />
                                <p className="text-sm text-[#F5F7FA] text-opacity-80" data-id="element-3881">
                                  <span className="font-medium" data-id="element-3882">
                                    Key insight:
                                  </span>{' '}
                                  Main point of contact for implementation.
                                  Concerned about timeline delays with the API
                                  integration.
                                </p>
                              </div>
                            </div>
                          </div>
                          <button className="flex items-center text-[#C8F169] text-sm font-medium" data-id="element-3883">
                            View all 5 attendees
                            <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-3884" />
                          </button>
                        </div>
                      </div>
                      {/* Business Insights */}
                      <div className="p-6 border-b border-[#2A3544]" data-id="element-3885">
                        <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4 flex items-center" data-id="element-3886">
                          <BarChartIcon className="h-4 w-4 mr-1.5 text-[#C8F169]" data-id="element-3887" />
                          Business Insights
                        </h4>
                        <div className="space-y-4" data-id="element-3888">
                          <div className="flex items-start" data-id="element-3889">
                            <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 mr-3 flex-shrink-0" data-id="element-3890" />
                            <div data-id="element-3891">
                              <p className="text-[#F5F7FA] font-medium mb-1" data-id="element-3892">
                                Company grew 27% in Q2, exceeding the market
                                average of 18%
                              </p>
                              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3893">
                                Their growth is primarily driven by expansion
                                into enterprise accounts and their new cloud
                                offerings.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-3894">
                            <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 mr-3 flex-shrink-0" data-id="element-3895" />
                            <div data-id="element-3896">
                              <p className="text-[#F5F7FA] font-medium mb-1" data-id="element-3897">
                                API integration timeline has been delayed by 2
                                weeks due to technical challenges
                              </p>
                              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3898">
                                The team has been struggling with authentication
                                issues and data mapping complexities.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-3899">
                            <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 mr-3 flex-shrink-0" data-id="element-3900" />
                            <div data-id="element-3901">
                              <p className="text-[#F5F7FA] font-medium mb-1" data-id="element-3902">
                                Customer satisfaction score improved by 12%
                                following the last implementation phase
                              </p>
                              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-3903">
                                The new dashboard features have been
                                particularly well-received by end users.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Talking Points */}
                      <div className="p-6 border-b border-[#2A3544]" data-id="element-3904">
                        <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4 flex items-center" data-id="element-3905">
                          <FileTextIcon className="h-4 w-4 mr-1.5 text-[#C8F169]" data-id="element-3906" />
                          Suggested Talking Points
                        </h4>
                        <div className="space-y-4" data-id="element-3907">
                          <div className="flex items-start" data-id="element-3908">
                            <div className="w-6 h-6 rounded-full bg-[#1E2A38] border border-[#C8F169] flex items-center justify-center text-[#C8F169] text-sm font-medium mr-3 flex-shrink-0" data-id="element-3909">
                              1
                            </div>
                            <p className="text-[#F5F7FA]" data-id="element-3910">
                              Congratulate the team on their Q2 growth and
                              discuss how our solution contributed to their
                              success.
                            </p>
                          </div>
                          <div className="flex items-start" data-id="element-3911">
                            <div className="w-6 h-6 rounded-full bg-[#1E2A38] border border-[#C8F169] flex items-center justify-center text-[#C8F169] text-sm font-medium mr-3 flex-shrink-0" data-id="element-3912">
                              2
                            </div>
                            <p className="text-[#F5F7FA]" data-id="element-3913">
                              Address the API integration delays directly and
                              present our updated timeline with additional
                              resources.
                            </p>
                          </div>
                          <div className="flex items-start" data-id="element-3914">
                            <div className="w-6 h-6 rounded-full bg-[#1E2A38] border border-[#C8F169] flex items-center justify-center text-[#C8F169] text-sm font-medium mr-3 flex-shrink-0" data-id="element-3915">
                              3
                            </div>
                            <p className="text-[#F5F7FA]" data-id="element-3916">
                              Explore their interest in the new analytics
                              module, which aligns with their data-driven
                              approach.
                            </p>
                          </div>
                          <div className="flex items-start" data-id="element-3917">
                            <div className="w-6 h-6 rounded-full bg-[#1E2A38] border border-[#C8F169] flex items-center justify-center text-[#C8F169] text-sm font-medium mr-3 flex-shrink-0" data-id="element-3918">
                              4
                            </div>
                            <p className="text-[#F5F7FA]" data-id="element-3919">
                              Discuss Q3 roadmap priorities and ensure alignment
                              with their business objectives.
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="p-6" data-id="element-3920">
                        <div className="flex items-center justify-between" data-id="element-3921">
                          <button className="bg-[#C8F169] hover:bg-opacity-90 transition-colors text-[#1E2A38] px-5 py-2.5 rounded-lg font-medium flex items-center" data-id="element-3922">
                            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-3923" />
                            View Full Brief
                          </button>
                          <div className="flex items-center space-x-3" data-id="element-3924">
                            <button className="bg-[#1E2A38] hover:bg-[#263548] transition-colors px-4 py-2.5 rounded-lg font-medium flex items-center text-[#F5F7FA]" data-id="element-3925">
                              <MailIcon className="h-4 w-4 mr-2" data-id="element-3926" />
                              Share
                            </button>
                            <button className="bg-[#1E2A38] hover:bg-[#263548] transition-colors px-4 py-2.5 rounded-lg font-medium flex items-center text-[#F5F7FA]" data-id="element-3927">
                              <SearchIcon className="h-4 w-4 mr-2" data-id="element-3928" />
                              Research More
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Progress indicators */}
            <div className="flex justify-center space-x-3 mt-6" data-id="element-3929">
              {Array.from({
              length: 5
            }).map((_, index) => <button key={index} onClick={() => setAnimationStep(index)} className={`relative h-2 rounded-full transition-all duration-300 ${animationStep === index ? 'w-10 bg-[#C8F169]' : animationStep > index ? 'w-2 bg-[#C8F169] bg-opacity-70' : 'w-2 bg-[#2A3544]'}`} data-id="element-3930">
                  <span className="sr-only" data-id="element-3931">Step {index + 1}</span>
                </button>)}
            </div>
          </div>
          {/* Step 2: Review Extracted Data - Only show if animation is complete */}
          {animationComplete && extractedData && <div className="bg-[#1A2331] rounded-xl p-6" data-id="element-3932">
              <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-3933">
                <ClipboardIcon className="mr-2 h-5 w-5 text-[#C8F169]" data-id="element-3934" />
                Step 2: Review & Edit Extracted Information
              </h2>
              <div className="space-y-6" data-id="element-3935">
                <div data-id="element-3936">
                  <label className="block text-sm font-medium text-[#F5F7FA] text-opacity-70 mb-2" data-id="element-3937">
                    Meeting Subject
                  </label>
                  <input type="text" value={extractedData.subject} onChange={e => setExtractedData({
                ...extractedData,
                subject: e.target.value
              })} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-20 rounded-lg px-4 py-2 text-[#F5F7FA]" data-id="element-3938" />
                </div>
                <div data-id="element-3939">
                  <label className="block text-sm font-medium text-[#F5F7FA] text-opacity-70 mb-2" data-id="element-3940">
                    Date & Time
                  </label>
                  <input type="text" value={extractedData.dateTime} onChange={e => setExtractedData({
                ...extractedData,
                dateTime: e.target.value
              })} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-20 rounded-lg px-4 py-2 text-[#F5F7FA]" data-id="element-3941" />
                </div>
                <div data-id="element-3942">
                  <div className="flex justify-between items-center mb-2" data-id="element-3943">
                    <label className="block text-sm font-medium text-[#F5F7FA] text-opacity-70" data-id="element-3944">
                      Attendees
                    </label>
                    <button onClick={addAttendee} className="text-[#C8F169] text-sm flex items-center" data-id="element-3945">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-3946" /> Add Attendee
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-3947">
                    {extractedData.attendees.map((attendee, index) => <div key={index} className="bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg p-4" data-id="element-3948">
                        <div className="flex justify-between mb-3" data-id="element-3949">
                          <h4 className="font-medium" data-id="element-3950">Attendee #{index + 1}</h4>
                          <button onClick={() => removeAttendee(index)} className="text-[#F5F7FA] text-opacity-70 hover:text-[#C8F169]" data-id="element-3951">
                            <XIcon className="h-4 w-4" data-id="element-3952" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3953">
                          <div data-id="element-3954">
                            <label className="block text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-3955">
                              Name
                            </label>
                            <input type="text" value={attendee.name} onChange={e => updateAttendee(index, 'name', e.target.value)} className="w-full bg-[#1E2A38] border border-[#F5F7FA] border-opacity-20 rounded-lg px-3 py-1.5 text-sm text-[#F5F7FA]" data-id="element-3956" />
                          </div>
                          <div data-id="element-3957">
                            <label className="block text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-3958">
                              Email
                            </label>
                            <input type="email" value={attendee.email} onChange={e => updateAttendee(index, 'email', e.target.value)} className="w-full bg-[#1E2A38] border border-[#F5F7FA] border-opacity-20 rounded-lg px-3 py-1.5 text-sm text-[#F5F7FA]" data-id="element-3959" />
                          </div>
                          <div data-id="element-3960">
                            <label className="block text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-3961">
                              Role (optional)
                            </label>
                            <input type="text" value={attendee.role || ''} onChange={e => updateAttendee(index, 'role', e.target.value)} className="w-full bg-[#1E2A38] border border-[#F5F7FA] border-opacity-20 rounded-lg px-3 py-1.5 text-sm text-[#F5F7FA]" data-id="element-3962" />
                          </div>
                          <div data-id="element-3963">
                            <label className="block text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-3964">
                              Company (optional)
                            </label>
                            <input type="text" value={attendee.company || ''} onChange={e => updateAttendee(index, 'company', e.target.value)} className="w-full bg-[#1E2A38] border border-[#F5F7FA] border-opacity-20 rounded-lg px-3 py-1.5 text-sm text-[#F5F7FA]" data-id="element-3965" />
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div data-id="element-3966">
                  <label className="block text-sm font-medium text-[#F5F7FA] text-opacity-70 mb-2" data-id="element-3967">
                    Meeting Description
                  </label>
                  <textarea value={extractedData.description} onChange={e => setExtractedData({
                ...extractedData,
                description: e.target.value
              })} rows={4} className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-20 rounded-lg px-4 py-2 text-[#F5F7FA]" data-id="element-3968"></textarea>
                </div>
                <div className="border-t border-[#F5F7FA] border-opacity-10 pt-6" data-id="element-3969">
                  <h3 className="font-medium mb-3" data-id="element-3970">Select Persona for Brief</h3>
                  <div className="flex flex-wrap gap-3" data-id="element-3971">
                    <button className={`px-4 py-2 rounded-lg text-sm ${selectedPersona === 'sales' ? 'bg-[#C8F169] text-[#1E2A38]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA]'}`} onClick={() => setSelectedPersona('sales')} data-id="element-3972">
                      Sales
                    </button>
                    <button className={`px-4 py-2 rounded-lg text-sm ${selectedPersona === 'customerService' ? 'bg-[#C8F169] text-[#1E2A38]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA]'}`} onClick={() => setSelectedPersona('customerService')} data-id="element-3973">
                      Customer Service
                    </button>
                    <button className={`px-4 py-2 rounded-lg text-sm ${selectedPersona === 'consultant' ? 'bg-[#C8F169] text-[#1E2A38]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA]'}`} onClick={() => setSelectedPersona('consultant')} data-id="element-3974">
                      Consultant
                    </button>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-3975">
                  <button className={`bg-[#C8F169] text-[#1E2A38] px-6 py-3 rounded-lg font-medium flex items-center ${isGeneratingBrief ? 'opacity-70 cursor-not-allowed' : 'hover:bg-opacity-90'}`} onClick={handleGenerateBrief} disabled={isGeneratingBrief} data-id="element-3976">
                    {isGeneratingBrief ? <>
                        <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-3977" />
                        Generating Brief...
                      </> : <>Generate Meeting Brief</>}
                  </button>
                </div>
              </div>
            </div>}
          {/* Step 3: Generated Brief */}
          {generatedBrief && <div className="bg-[#1A2331] rounded-xl p-6" data-id="element-3978">
              <h2 className="text-xl font-semibold mb-6 flex items-center" data-id="element-3979">
                <FileTextIcon className="mr-2 h-5 w-5 text-[#C8F169]" data-id="element-3980" />
                Meeting Brief: {extractedData?.subject}
              </h2>
              <div className="space-y-8" data-id="element-3981">
                {/* Company Insights */}
                <div data-id="element-3982">
                  <h3 className="text-lg font-medium mb-4 text-[#C8F169]" data-id="element-3983">
                    Company Insights
                  </h3>
                  {generatedBrief.companyInsights.map((company, index) => <div key={index} className="bg-[#141E2A] rounded-lg p-5 mb-4" data-id="element-3984">
                      <div className="flex justify-between mb-3" data-id="element-3985">
                        <h4 className="font-medium text-lg" data-id="element-3986">{company.name}</h4>
                        {company.isMock && <span className="bg-yellow-500 bg-opacity-20 text-yellow-400 text-xs px-2 py-1 rounded-full" data-id="element-3987">
                            Simulated Data
                          </span>}
                      </div>
                      <p className="text-[#F5F7FA] text-opacity-90 mb-4" data-id="element-3988">
                        {company.description}
                      </p>
                      <div className="mb-4" data-id="element-3989">
                        <h5 className="text-sm font-medium text-[#F5F7FA] text-opacity-70 mb-2" data-id="element-3990">
                          Recent News
                        </h5>
                        <ul className="space-y-1" data-id="element-3991">
                          {company.recentNews.map((news, i) => <li key={i} className="flex items-start" data-id="element-3992">
                              <span className="text-[#C8F169] mr-2" data-id="element-3993">•</span>
                              <span data-id="element-3994">{news}</span>
                            </li>)}
                        </ul>
                      </div>
                      {company.linkedInUrl && <a href={company.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-[#C8F169] flex items-center text-sm" data-id="element-3995">
                          <ExternalLinkIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-3996" />
                          View on LinkedIn
                        </a>}
                      <div className="mt-3 pt-3 border-t border-[#F5F7FA] border-opacity-10 text-sm" data-id="element-3997">
                        <div className="flex items-center" data-id="element-3998">
                          <span className="text-[#F5F7FA] text-opacity-70 mr-2" data-id="element-3999">
                            Confidence:
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${company.confidenceLevel === 'High' ? 'bg-green-500 bg-opacity-20 text-green-400' : company.confidenceLevel === 'Medium' ? 'bg-yellow-500 bg-opacity-20 text-yellow-400' : 'bg-red-500 bg-opacity-20 text-red-400'}`} data-id="element-4000">
                            {company.confidenceLevel}
                          </span>
                        </div>
                        {company.confidenceLevel !== 'High' && <div className="mt-2 text-yellow-400 text-xs italic" data-id="element-4001">
                            Note: Some information may be incomplete or require
                            verification.
                          </div>}
                      </div>
                    </div>)}
                </div>
                {/* Attendee Breakdown */}
                <div data-id="element-4002">
                  <h3 className="text-lg font-medium mb-4 text-[#C8F169]" data-id="element-4003">
                    Attendee Breakdown
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4004">
                    {generatedBrief.attendeeBreakdown.map((attendee, index) => <div key={index} className="bg-[#141E2A] rounded-lg p-4" data-id="element-4005">
                        <div className="flex items-start mb-3" data-id="element-4006">
                          <div className="h-10 w-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] font-bold mr-3" data-id="element-4007">
                            {attendee.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-4008">
                            <h4 className="font-medium" data-id="element-4009">{attendee.name}</h4>
                            <div className="flex items-center text-sm text-[#F5F7FA] text-opacity-70" data-id="element-4010">
                              <span data-id="element-4011">{attendee.role}</span>
                              {!attendee.isRoleConfirmed && <span className="ml-2 text-xs bg-yellow-500 bg-opacity-20 text-yellow-400 px-1.5 py-0.5 rounded-full" data-id="element-4012">
                                  Role unconfirmed
                                </span>}
                            </div>
                            <div className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-4013">
                              {attendee.company}
                            </div>
                          </div>
                        </div>
                        {attendee.background && <div className="mb-3" data-id="element-4014">
                            <h5 className="text-xs text-[#F5F7FA] text-opacity-70 mb-1" data-id="element-4015">
                              Background
                            </h5>
                            <p className="text-sm" data-id="element-4016">{attendee.background}</p>
                          </div>}
                        {attendee.linkedInUrl && <a href={attendee.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-[#C8F169] flex items-center text-xs" data-id="element-4017">
                            <ExternalLinkIcon className="h-3 w-3 mr-1" data-id="element-4018" />
                            LinkedIn Profile
                          </a>}
                      </div>)}
                  </div>
                </div>
                {/* Persona-Specific Section */}
                <div data-id="element-4019">
                  <h3 className="text-lg font-medium mb-4 text-[#C8F169]" data-id="element-4020">
                    {generatedBrief.personaSection.type === 'sales' ? 'Sales Strategy' : generatedBrief.personaSection.type === 'customerService' ? 'Customer Service Overview' : 'Consulting Framework'}
                  </h3>
                  <div className="bg-[#141E2A] rounded-lg p-5" data-id="element-4021">
                    {generatedBrief.personaSection.type === 'sales' && <div className="space-y-5" data-id="element-4022">
                        <div data-id="element-4023">
                          <h4 className="font-medium mb-3" data-id="element-4024">
                            Potential Objections & Counters
                          </h4>
                          <ul className="space-y-2" data-id="element-4025">
                            {generatedBrief.personaSection.content.potentialObjections.map((objection, i) => <li key={i} className="flex items-start" data-id="element-4026">
                                  <AlertTriangleIcon className="h-4 w-4 text-yellow-400 mr-2 mt-0.5" data-id="element-4027" />
                                  <span data-id="element-4028">{objection}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4029">
                          <h4 className="font-medium mb-3" data-id="element-4030">
                            Discovery Questions
                          </h4>
                          <ul className="space-y-2" data-id="element-4031">
                            {generatedBrief.personaSection.content.discoveryQuestions.map((question, i) => <li key={i} className="flex items-start" data-id="element-4032">
                                  <span className="text-[#C8F169] mr-2" data-id="element-4033">•</span>
                                  <span data-id="element-4034">{question}</span>
                                </li>)}
                          </ul>
                        </div>
                      </div>}
                    {generatedBrief.personaSection.type === 'customerService' && <div className="space-y-5" data-id="element-4035">
                        <div data-id="element-4036">
                          <h4 className="font-medium mb-3" data-id="element-4037">Open Cases</h4>
                          <div className="space-y-2" data-id="element-4038">
                            {generatedBrief.personaSection.content.openCases.map((caseItem, i) => <div key={i} className="bg-[#1E2A38] p-3 rounded-lg" data-id="element-4039">
                                  <div className="flex justify-between mb-1" data-id="element-4040">
                                    <span className="font-medium" data-id="element-4041">
                                      {caseItem.id}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${caseItem.priority === 'High' ? 'bg-red-500 bg-opacity-20 text-red-400' : caseItem.priority === 'Medium' ? 'bg-yellow-500 bg-opacity-20 text-yellow-400' : 'bg-green-500 bg-opacity-20 text-green-400'}`} data-id="element-4042">
                                      {caseItem.priority}
                                    </span>
                                  </div>
                                  <p className="text-sm mb-1" data-id="element-4043">
                                    {caseItem.title}
                                  </p>
                                  <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-4044">
                                    Opened: {caseItem.openedDate}
                                  </p>
                                </div>)}
                          </div>
                        </div>
                        <div data-id="element-4045">
                          <h4 className="font-medium mb-3" data-id="element-4046">Customer Health</h4>
                          <div className="bg-[#1E2A38] p-3 rounded-lg" data-id="element-4047">
                            <div className="flex items-center mb-2" data-id="element-4048">
                              <span className="font-medium mr-2" data-id="element-4049">
                                Health Score:
                              </span>
                              <span className="bg-yellow-500 bg-opacity-20 text-yellow-400 px-2 py-0.5 rounded-full text-sm" data-id="element-4050">
                                {generatedBrief.personaSection.content.customerHealth.score}
                              </span>
                            </div>
                            <p className="text-sm" data-id="element-4051">
                              {generatedBrief.personaSection.content.customerHealth.notes}
                            </p>
                          </div>
                        </div>
                        <div data-id="element-4052">
                          <h4 className="font-medium mb-3" data-id="element-4053">Support Risks</h4>
                          <ul className="space-y-2" data-id="element-4054">
                            {generatedBrief.personaSection.content.supportRisks.map((risk, i) => <li key={i} className="flex items-start" data-id="element-4055">
                                  <AlertTriangleIcon className="h-4 w-4 text-yellow-400 mr-2 mt-0.5" data-id="element-4056" />
                                  <span data-id="element-4057">{risk}</span>
                                </li>)}
                          </ul>
                        </div>
                      </div>}
                    {generatedBrief.personaSection.type === 'consultant' && <div className="space-y-5" data-id="element-4058">
                        <div data-id="element-4059">
                          <h4 className="font-medium mb-3" data-id="element-4060">
                            Discovery Framing
                          </h4>
                          <p className="text-[#F5F7FA] text-opacity-90" data-id="element-4061">
                            {generatedBrief.personaSection.content.discoveryFraming}
                          </p>
                        </div>
                        <div data-id="element-4062">
                          <h4 className="font-medium mb-3" data-id="element-4063">Risks</h4>
                          <ul className="space-y-2" data-id="element-4064">
                            {generatedBrief.personaSection.content.risks.map((risk, i) => <li key={i} className="flex items-start" data-id="element-4065">
                                  <AlertTriangleIcon className="h-4 w-4 text-yellow-400 mr-2 mt-0.5" data-id="element-4066" />
                                  <span data-id="element-4067">{risk}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4068">
                          <h4 className="font-medium mb-3" data-id="element-4069">Deliverables</h4>
                          <ul className="space-y-2" data-id="element-4070">
                            {generatedBrief.personaSection.content.deliverables.map((deliverable, i) => <li key={i} className="flex items-start" data-id="element-4071">
                                  <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5" data-id="element-4072" />
                                  <span data-id="element-4073">{deliverable}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4074">
                          <h4 className="font-medium mb-3" data-id="element-4075">
                            Key Performance Indicators
                          </h4>
                          <ul className="space-y-2" data-id="element-4076">
                            {generatedBrief.personaSection.content.kpis.map((kpi, i) => <li key={i} className="flex items-start" data-id="element-4077">
                                  <span className="text-[#C8F169] mr-2" data-id="element-4078">•</span>
                                  <span data-id="element-4079">{kpi}</span>
                                </li>)}
                          </ul>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Previous Engagement */}
                <div data-id="element-4080">
                  <h3 className="text-lg font-medium mb-4 text-[#C8F169]" data-id="element-4081">
                    Previous Engagement
                  </h3>
                  <div className="bg-[#141E2A] rounded-lg p-5" data-id="element-4082">
                    {generatedBrief.previousEngagement.isLimited && <div className="bg-yellow-500 bg-opacity-10 border border-yellow-500 border-opacity-20 text-yellow-400 p-3 rounded-lg mb-4" data-id="element-4083">
                        <div className="flex items-center" data-id="element-4084">
                          <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-4085" />
                          <span data-id="element-4086">
                            Interaction history limited. Some data may be
                            incomplete.
                          </span>
                        </div>
                      </div>}
                    <div className="space-y-4" data-id="element-4087">
                      {generatedBrief.previousEngagement.interactions.map((interaction, i) => <div key={i} className="flex" data-id="element-4088">
                            <div className="mr-4 flex flex-col items-center" data-id="element-4089">
                              <div className="h-6 w-6 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-medium" data-id="element-4090">
                                {i + 1}
                              </div>
                              {i < generatedBrief.previousEngagement.interactions.length - 1 && <div className="w-0.5 h-full bg-[#F5F7FA] bg-opacity-10 my-1" data-id="element-4091"></div>}
                            </div>
                            <div data-id="element-4092">
                              <div className="text-sm font-medium" data-id="element-4093">
                                {interaction.date}
                              </div>
                              <p className="text-sm text-[#F5F7FA] text-opacity-90" data-id="element-4094">
                                {interaction.summary}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
                {/* Transcript Insights & Commitments */}
                <div data-id="element-4095">
                  <h3 className="text-lg font-medium mb-4 text-[#C8F169]" data-id="element-4096">
                    Transcript Insights & Commitments
                  </h3>
                  <div className="bg-[#141E2A] rounded-lg p-5" data-id="element-4097">
                    {!generatedBrief.transcriptInsights.hasPreviousTranscripts ? <div className="flex items-center text-red-400" data-id="element-4098">
                        <XIcon className="h-5 w-5 mr-2" data-id="element-4099" />
                        <span data-id="element-4100">No previous transcripts have been found</span>
                      </div> : <div className="space-y-5" data-id="element-4101">
                        <div data-id="element-4102">
                          <h4 className="font-medium mb-3" data-id="element-4103">
                            Outstanding Commitments
                          </h4>
                          <ul className="space-y-2" data-id="element-4104">
                            {generatedBrief.transcriptInsights.outstandingCommitments.map((commitment, i) => <li key={i} className="flex items-start" data-id="element-4105">
                                  <span className="text-red-400 mr-2" data-id="element-4106">🚨</span>
                                  <span data-id="element-4107">{commitment}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4108">
                          <h4 className="font-medium mb-3" data-id="element-4109">Pending Items</h4>
                          <ul className="space-y-2" data-id="element-4110">
                            {generatedBrief.transcriptInsights.pendingItems.map((item, i) => <li key={i} className="flex items-start" data-id="element-4111">
                                  <span className="text-yellow-400 mr-2" data-id="element-4112">
                                    ⚠️
                                  </span>
                                  <span data-id="element-4113">{item}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4114">
                          <h4 className="font-medium mb-3" data-id="element-4115">Resolved Items</h4>
                          <ul className="space-y-2" data-id="element-4116">
                            {generatedBrief.transcriptInsights.resolvedItems.map((item, i) => <li key={i} className="flex items-start" data-id="element-4117">
                                  <span className="text-green-400 mr-2" data-id="element-4118">
                                    ✅
                                  </span>
                                  <span data-id="element-4119">{item}</span>
                                </li>)}
                          </ul>
                        </div>
                        <div data-id="element-4120">
                          <h4 className="font-medium mb-3" data-id="element-4121">
                            Sentiment Highlights
                          </h4>
                          <ul className="space-y-2" data-id="element-4122">
                            {generatedBrief.transcriptInsights.sentimentHighlights.map((highlight, i) => <li key={i} className="flex items-start" data-id="element-4123">
                                  <UserIcon className="h-4 w-4 text-[#C8F169] mr-2 mt-0.5" data-id="element-4124" />
                                  <span data-id="element-4125">{highlight}</span>
                                </li>)}
                          </ul>
                        </div>
                      </div>}
                  </div>
                </div>
                <div className="flex justify-end pt-4 border-t border-[#F5F7FA] border-opacity-10" data-id="element-4126">
                  <button className="bg-[#C8F169] hover:bg-opacity-90 text-[#1E2A38] px-4 py-2 rounded-lg text-sm font-medium" data-id="element-4127">
                    Save Brief
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </main>
    </div>;
}